/** 每个文档内独立去重的两个分析脚本。 */
export type AnalyticsScript = 'sensors' | 'classic';

/** 双逻辑消费者共用的唯一分析资源地址。 */
const ANALYTICS_SCRIPT_URL = '/d7735a.js';

/** 一个脚本的等待结果及可撤销监听。 */
interface ScriptEntry {
  promise: Promise<void>;
  cancel: () => void;
}

/** 文档销毁后保持终态，禁止迟到回调或新调用恢复加载。 */
interface DocumentScripts {
  disposed: boolean;
  entries: Map<string, ScriptEntry>;
}

/** 按文档保存有限状态，避免跨文档复用已销毁的脚本。 */
const documents = new WeakMap<Document, DocumentScripts>();

/**
 * 判断两份真实库是否均已执行完成，不判断神策身份或创建 Tracker。
 * @returns 已加载神策 init/quick 与 Classic 对象命令入口时为 true；读取异常为 false。
 */
function isUnifiedAnalyticsLoaded(): boolean {
  try {
    const sensors = Reflect.get(window, 'sensorsDataAnalytic201505');
    const classic = Reflect.get(window, 'snowplow');
    return (
      typeof sensors?.init === 'function' &&
      typeof sensors?.quick === 'function' &&
      typeof classic === 'function' &&
      typeof classic.q === 'object' &&
      classic.q !== null &&
      !Array.isArray(classic.q) &&
      typeof classic.q.push === 'function'
    );
  } catch {
    return false;
  }
}

/**
 * 为神策及 Classic 消费者复用唯一物理加载，明确失败后允许重试。
 * @param kind 逻辑消费者名称，保留现有调用接口；两者读取同一个资源。
 * @returns 资源执行完成的 Promise，不代表神策初始化或真实身份就绪。
 */
export function loadAnalyticsScript(_kind: AnalyticsScript): Promise<void> {
  if (typeof document === 'undefined')
    return Promise.reject(new Error('analytics_document_missing'));
  const ownerDocument = document;
  let state = documents.get(ownerDocument);
  if (!state) {
    state = { disposed: false, entries: new Map() };
    documents.set(ownerDocument, state);
  }
  if (state.disposed) return Promise.reject(new Error('analytics_script_loader_disposed'));
  const existing = state.entries.get('unified');
  if (existing) return existing.promise;

  // 先登记状态再操作DOM，兼容同步触发load的测试或宿主环境。
  let resolve!: () => void;
  let reject!: (error: Error) => void;
  const promise = new Promise<void>((onResolve, onReject) => {
    resolve = onResolve;
    reject = onReject;
  });
  let settled = false;
  let script: HTMLScriptElement | undefined;
  const entry: ScriptEntry = { promise, cancel };
  state.entries.set('unified', entry);

  /** 只解除本站点附加的监听，保留旧入口自己的处理器。 */
  function detach(): void {
    script?.removeEventListener('load', onLoad);
    script?.removeEventListener('error', onError);
  }
  /** load仅表示脚本完成；神策初始化及身份由独立门禁确认。 */
  function onLoad(): void {
    if (settled || state!.disposed) return;
    if (!isUnifiedAnalyticsLoaded()) {
      onError();
      return;
    }
    settled = true;
    detach();
    resolve();
  }
  /** 明确失败清除节点与单例记录，交给编排器决定重试时机。 */
  function onError(): void {
    if (settled || state!.disposed) return;
    settled = true;
    detach();
    script?.remove();
    state!.entries.delete('unified');
    reject(new Error('analytics_script_load_failed'));
  }
  /** 文档销毁取消等待，但不通过删除pending节点触发第二次加载。 */
  function cancel(): void {
    detach();
    if (settled) return;
    settled = true;
    reject(new Error('analytics_script_loader_disposed'));
  }

  try {
    // SSR 已执行资源时不能等待已经发生的 load；初始化门控仍由调用方负责。
    if (isUnifiedAnalyticsLoaded()) {
      settled = true;
      resolve();
      return promise;
    }
    const url = ANALYTICS_SCRIPT_URL;
    // 集成切换前复用layout的相同URL节点，不能并行插入覆盖SDK全局对象。
    script = Array.from(ownerDocument.querySelectorAll<HTMLScriptElement>('script[src]')).find(
      (candidate) => candidate.src === new URL(url, ownerDocument.baseURI).href,
    );
    const needsInsertion = !script;
    script ??= ownerDocument.createElement('script');
    script.setAttribute('data-zl-analytics', 'unified');
    script.addEventListener('load', onLoad);
    script.addEventListener('error', onError);
    if (needsInsertion) {
      script.async = true;
      script.src = url;
      ownerDocument.head.appendChild(script);
    }
  } catch {
    onError();
  }
  return promise;
}

/**
 * 页面销毁时取消文档内资源监听，保持销毁终态。
 * @returns 无返回值；无入参，重复调用幂等，SSR 不访问浏览器对象。
 */
export function disposeAnalyticsScriptLoader(): void {
  if (typeof document === 'undefined') return;
  let state = documents.get(document);
  if (!state) {
    state = { disposed: true, entries: new Map() };
    documents.set(document, state);
  }
  state.disposed = true;
  for (const entry of Array.from(state.entries.values())) entry.cancel();
}
